import React, { useContext } from 'react';

import { theme } from 'config/theme';
import { CanvasUnitContext, fitLabel, uprightLabelBox } from '../canvasScale';
import { featureName, MapFeature } from '../features';

const SELECTED_COLOR = theme.palette.primary.main;

export const FEATURE_FILL = '#D5D3D4';
const FEATURE_LINE = '#B5B3B4';
const ROUNDED_CORNER = 3;
const STAIR_TREAD_METERS = 0.3;
const LABEL_COLOR = '#424242';
const LABEL_MAX_FONT = 9;
const STAIRS_LABEL_GAP = 8;

function StairsBody({
    width,
    height,
    pxPerMeter,
    outline,
    outlineWidth,
    unit,
}: {
    width: number;
    height: number;
    pxPerMeter: number;
    outline: string;
    outlineWidth: number;
    unit: number;
}) {
    const isVertical = height >= width;
    const length = isVertical ? height : width;
    const step = STAIR_TREAD_METERS * pxPerMeter;
    const treads = Math.max(0, Math.ceil(length / step - 1e-6) - 1);
    return (
        <>
            <rect
                x={-width / 2}
                y={-height / 2}
                width={width}
                height={height}
                rx={ROUNDED_CORNER * unit}
                fill={FEATURE_FILL}
                stroke={outline}
                strokeWidth={outlineWidth}
            />
            {Array.from({ length: treads }, (_, i) => {
                const at = -length / 2 + (i + 1) * step;
                return isVertical ? (
                    <line
                        key={i}
                        x1={-width / 2}
                        y1={at}
                        x2={width / 2}
                        y2={at}
                        stroke={FEATURE_LINE}
                        strokeWidth={unit}
                    />
                ) : (
                    <line
                        key={i}
                        x1={at}
                        y1={-height / 2}
                        x2={at}
                        y2={height / 2}
                        stroke={FEATURE_LINE}
                        strokeWidth={unit}
                    />
                );
            })}
        </>
    );
}

export default function FeatureFigure({
    feature,
    pxPerMeter,
    isSelected = false,
}: {
    feature: MapFeature;
    pxPerMeter: number;
    isSelected?: boolean;
}) {
    const unit = useContext(CanvasUnitContext);
    const width = feature.width * pxPerMeter;
    const height = feature.height * pxPerMeter;
    const stroke = isSelected ? SELECTED_COLOR : 'none';
    const strokeWidth = (isSelected ? 2 : 1) * unit;
    const rad = (feature.rotation * Math.PI) / 180;
    const halfExtentY = (Math.abs(width * Math.sin(rad)) + Math.abs(height * Math.cos(rad))) / 2;
    const isNamed = feature.kind !== 'Pillar' && feature.kind !== 'Wall';
    const isLabelAbove = feature.kind === 'Stairs';
    const name = featureName(feature);
    const labelBox = uprightLabelBox(width, height, feature.rotation);
    const fitted = isNamed
        ? fitLabel(
              name.toUpperCase(),
              isLabelAbove ? { width: labelBox.width, height: Infinity } : labelBox,
              LABEL_MAX_FONT,
              unit
          )
        : null;

    function renderBody() {
        if (feature.kind === 'Stairs') {
            return (
                <StairsBody
                    width={width}
                    height={height}
                    pxPerMeter={pxPerMeter}
                    outline={stroke}
                    outlineWidth={strokeWidth}
                    unit={unit}
                />
            );
        }
        if (feature.kind === 'Pillar' && feature.isRound) {
            return (
                <ellipse
                    rx={width / 2}
                    ry={height / 2}
                    fill={FEATURE_FILL}
                    stroke={stroke}
                    strokeWidth={strokeWidth}
                />
            );
        }
        if (feature.kind === 'Custom' && feature.points) {
            const points = feature.points
                .map((p) => `${(p.x - 0.5) * width},${(p.y - 0.5) * height}`)
                .join(' ');
            return (
                <polygon
                    points={points}
                    fill={FEATURE_FILL}
                    stroke={isSelected ? SELECTED_COLOR : FEATURE_LINE}
                    strokeWidth={(isSelected ? 2.5 : 1) * unit}
                    strokeDasharray={`${4 * unit} ${3 * unit}`}
                    strokeLinejoin="round"
                />
            );
        }
        const isRounded = feature.kind === 'BarCounter' || feature.kind === 'Furniture';
        return (
            <rect
                x={-width / 2}
                y={-height / 2}
                width={width}
                height={height}
                rx={isRounded ? ROUNDED_CORNER * unit : 0}
                fill={FEATURE_FILL}
                stroke={stroke}
                strokeWidth={strokeWidth}
            />
        );
    }

    return (
        <g>
            {isNamed && <title>{name}</title>}
            <g transform={feature.rotation ? `rotate(${feature.rotation})` : undefined}>
                {renderBody()}
            </g>
            {fitted && (
                <text
                    y={isLabelAbove ? -halfExtentY - STAIRS_LABEL_GAP * unit : 0}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={fitted.fontSize}
                    fontWeight={600}
                    letterSpacing={fitted.fontSize / 18}
                    fill={LABEL_COLOR}
                    stroke="white"
                    strokeWidth={fitted.fontSize / 3}
                    paintOrder="stroke"
                    fontFamily="-apple-system, BlinkMacSystemFont, Roboto, Segoe UI, sans-serif"
                    style={{ userSelect: 'none', pointerEvents: 'none' }}
                >
                    {fitted.text}
                </text>
            )}
        </g>
    );
}
